import { useMutation } from "@apollo/client";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import ThumbDownAlt from "@mui/icons-material/ThumbDownAlt";
import ThumbDownAltOutlined from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";
import ThumbUpAltOutlined from "@mui/icons-material/ThumbUpAltOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Stack } from "@mui/system";
import ScoutIcon from "components/ScoutIcon";
import { gql } from "generated-graphql";
import { ScoutMessageFeedbackRating } from "generated-graphql/graphql";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { memo, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ToolCallGroup } from "../ToolCallGroup";
import { MessageContent } from "./MessageContent";
import {
  getScoutMessageText,
  ScoutChatMessage,
  ScoutChatMessagePart,
  ScoutTextPart,
  ScoutToolPart,
} from "./scoutChatMessage";

const RATE_SCOUT_MESSAGE = gql(`
  mutation RateScoutMessage($input: RateScoutMessageInput!) {
    rateScoutMessage(input: $input) {
      id
      rating
      createdAt
      updatedAt
    }
  }
`);

/**
 * A render block is the smallest unit the agent message renders. We collapse
 * runs of like-kinded parts into one block so the layout matches the agent's
 * actual flow: a stretch of tool calls renders as a single grouped widget,
 * and a stretch of text renders as one prose chunk.
 *
 * **Reasoning is intentionally not rendered.** Anthropic's extended-thinking
 * blocks aren't reliably bound by the system prompt's customer-facing rules
 * (no internal tool names, no implementation talk, etc.) — the model
 * frequently mentions `execute_graphql`, internal table names, and other
 * mechanics in thinking even when its final answer is clean. Reasoning parts
 * still flow through the store so chronological ordering between text and
 * tools is preserved (the per-block id work in scoutConversationStore.ts);
 * we just don't surface the content here. If reasoning rendering is needed
 * later for staff/debug contexts, gate it behind a flag at this site.
 *
 * **Note on persistence.** Reasoning rows ARE persisted server-side as
 * `ScoutMessagePart` rows with `type='reasoning'` (see
 * `packages/encamp-scout-connection/src/scout/scout-conversation-materializer.ts`).
 * The hide-in-customer-UI policy is render-only. If reasoning content ever
 * has to be kept out of the database for compliance reasons (PII, leaked
 * mechanics that shouldn't survive logout, etc.), the right place to filter
 * is the materializer at write-time, not this file.
 *
 * Source / file / step parts are also dropped here for the same reason as
 * before — the customer UI doesn't surface them.
 */
type RenderBlock =
  | { kind: "text"; id: string; parts: ScoutTextPart[] }
  | { kind: "tool-group"; id: string; groupOrdinal: number; tools: ScoutToolPart[] };

const ASSISTANT_MESSAGE_PREFIX = "scout-assistant-";
const UUID_V4_OR_V5_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function resolveRateableMessageId(message: ScoutChatMessage): string | undefined {
  if (message.persistedMessageId && UUID_V4_OR_V5_REGEX.test(message.persistedMessageId)) {
    return message.persistedMessageId;
  }

  if (!message.id.startsWith(ASSISTANT_MESSAGE_PREFIX) && UUID_V4_OR_V5_REGEX.test(message.id)) {
    return message.id;
  }

  return undefined;
}

function groupParts(parts: ScoutChatMessagePart[]): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  let toolGroupOrdinal = 0;

  for (const part of parts) {
    const last = blocks[blocks.length - 1];

    if (part.type === "tool") {
      if (last?.kind === "tool-group") {
        last.tools.push(part);
      } else {
        blocks.push({
          kind: "tool-group",
          id: part.id,
          groupOrdinal: toolGroupOrdinal,
          tools: [part],
        });
        toolGroupOrdinal += 1;
      }
      continue;
    }

    if (part.type === "text") {
      if (last?.kind === "text") {
        last.parts.push(part);
      } else {
        blocks.push({ kind: "text", id: part.id, parts: [part] });
      }
      continue;
    }

    // Reasoning, source, file, step parts: intentionally not rendered. See
    // RenderBlock JSDoc for the rationale.
  }

  return blocks;
}

export const AgentMessage = memo(
  function AgentMessage({ message }: { message: ScoutChatMessage }) {
    const blocks = useMemo(() => groupParts(message.parts), [message.parts]);
    const [toolGroupExpandedState, setToolGroupExpandedState] = useState<Record<number, boolean>>(
      {}
    );

    const handleToolGroupExpandedChange = useCallback(
      (groupOrdinal: number, nextExpanded: boolean) => {
        setToolGroupExpandedState((current) => {
          if (current[groupOrdinal] === nextExpanded) {
            return current;
          }
          return { ...current, [groupOrdinal]: nextExpanded };
        });
      },
      []
    );

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          py: 1.75,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
          sx={{
            maxWidth: "90%",
            px: 2,
            py: 1.5,
            borderRadius: 3,
            bgcolor: "transparent",
            color: "text.primary",
            overflowWrap: "break-word",
          }}
        >
          <Box
            sx={{
              "@keyframes scoutSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            <ScoutIcon
              sx={{
                color: "#B0BEC5",
                animation: message.isStreaming ? "scoutSpin 1.1s linear infinite" : "none",
                transformOrigin: "center",
                mt: 0.125,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2.25}>
              {blocks.map((block) =>
                block.kind === "tool-group" ? (
                  <ToolCallGroup
                    key={block.id}
                    toolParts={block.tools}
                    isStreaming={message.isStreaming}
                    expanded={toolGroupExpandedState[block.groupOrdinal] ?? false}
                    onExpandedChange={(nextExpanded) =>
                      handleToolGroupExpandedChange(block.groupOrdinal, nextExpanded)
                    }
                  />
                ) : (
                  <MessageContent
                    key={block.id}
                    messageId={`${message.id}-${block.id}`}
                    parts={block.parts}
                    isStreaming={message.isStreaming}
                  />
                )
              )}
            </Stack>
            <Box sx={{ height: 10 }} />
            <AgentMessageToolbar message={message} />
          </Box>
        </Stack>
      </Box>
    );
  },
  (prev, next) =>
    prev.message.id === next.message.id &&
    prev.message.isStreaming === next.message.isStreaming &&
    prev.message.parts === next.message.parts
);
AgentMessage.displayName = "AgentMessage";

function AgentMessageToolbar({ message }: { message: ScoutChatMessage }) {
  const copyToClipboard = useCopyToClipboard();
  const handleCopy = useCallback(async () => {
    const text = getScoutMessageText(message);
    await copyToClipboard(text);
  }, [copyToClipboard, message]);

  if (message.isStreaming) {
    return null;
  }

  return (
    <Stack width="100%" direction="row" alignItems="center" spacing={1} sx={{ ml: -1 }}>
      <Tooltip title="Copy text">
        <IconButton aria-label="Copy text" size="small" onClick={handleCopy}>
          <CopyAllOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <FeedbackButtons message={message} />
    </Stack>
  );
}

function FeedbackButtons({ message }: { message: ScoutChatMessage }) {
  const { tenantId } = useParams();
  const [optimisticRating, setOptimisticRating] = useState<
    ScoutMessageFeedbackRating | null | undefined
  >(undefined);
  const [rateScoutMessage, { loading }] = useMutation(RATE_SCOUT_MESSAGE);
  const rateableMessageId = useMemo(() => resolveRateableMessageId(message), [message]);

  const currentRating: ScoutMessageFeedbackRating | null =
    optimisticRating !== undefined ? optimisticRating : (message.feedback?.rating ?? null);

  const submitRating = useCallback(
    async (rating: ScoutMessageFeedbackRating) => {
      if (!tenantId || !rateableMessageId) {
        return;
      }

      const previous = currentRating;
      setOptimisticRating(rating);
      try {
        await rateScoutMessage({
          variables: { input: { tenantId, messageId: rateableMessageId, rating } },
        });
      } catch {
        setOptimisticRating(previous);
      }
    },
    [currentRating, rateScoutMessage, rateableMessageId, tenantId]
  );

  const isThumbsUp = currentRating === ScoutMessageFeedbackRating.ThumbsUp;
  const isThumbsDown = currentRating === ScoutMessageFeedbackRating.ThumbsDown;

  return (
    <Stack direction="row" alignItems="center" spacing={-0.25}>
      <Tooltip title="Good response">
        <IconButton
          aria-label="Good response"
          aria-pressed={isThumbsUp}
          size="small"
          disabled={loading || !tenantId || !rateableMessageId}
          onClick={() => submitRating(ScoutMessageFeedbackRating.ThumbsUp)}
        >
          {isThumbsUp ? <ThumbUpAlt fontSize="small" /> : <ThumbUpAltOutlined fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Bad response">
        <IconButton
          aria-label="Bad response"
          aria-pressed={isThumbsDown}
          size="small"
          disabled={loading || !tenantId || !rateableMessageId}
          onClick={() => submitRating(ScoutMessageFeedbackRating.ThumbsDown)}
        >
          {isThumbsDown ? (
            <ThumbDownAlt fontSize="small" />
          ) : (
            <ThumbDownAltOutlined fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
